import React, { useEffect, useRef, useState } from 'react';
import ChatMessage from './ChatMessage'; 
import ChatInput from './ChatInput';
import UploadedImages from './UploadedImages';
import Modal from './Modal';
import { useAuth } from '../AuthContext';
import useSendMessages from '../hooks/useSendMessages';
import useFileUpload from '../hooks/useFileUpload';
import axios from "axios";
import API_URL from '../config/apiConfig';
import {
    DocumentCheckIcon,
    ChatBubbleLeftEllipsisIcon,
    XMarkIcon,
    PlusIcon,
    TrashIcon,
    PencilSquareIcon,
    CheckIcon,
    ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

const ChatContainer = ({ historyOpen, setHistoryOpen }) => {
    const { authToken } = useAuth();
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    
    // États de données
    const [conversations, setConversations] = useState([]);
    const [currentConversationId, setCurrentConversationId] = useState(null);

    // États pour le Titre et l'Édition
    const [currentTitle, setCurrentTitle] = useState("Nouvelle expertise");
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [editedTitle, setEditedTitle] = useState("");

    const defaultMessage = {
        role: "assistant",
        content: "Bonjour, je suis SophIA, l'assistante qui vous accompagne dans la tentative de résolution des dommages qui affectent le bien de votre client.\n\n" +
            "Pour m'aider à comprendre au mieux la situation et procéder efficacement à l'élaboration d'un pré-rapport, pourriez-vous me fournir une description détaillée de ce qu'il s'est passé ?"
    };

    const [sentImageUrls, setSentImageUrls] = useState([]);
    const [messages, setMessages] = useState([defaultMessage]);
    
    const messagesEndRef = useRef(null);
    const { uploadedImageUrls, setUploadedImageUrls, handleImageChange, removeFile, largeImageUrls } = useFileUpload();

    const userId = authToken?.userId;
    const [showModal, setShowModal] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [isOver, setIsOver] = useState(false);
    const [pdfStats, setPdfStats] = useState(null);
    const [isConversationCompleted, setIsConversationCompleted] = useState(false);
    const [currentPdfUrl, setCurrentPdfUrl] = useState(null);
    const [currentRating, setCurrentRating] = useState(null);
    const [hoverRating, setHoverRating] = useState(0);

    // --- 1. FONCTION DE CHARGEMENT DE L'HISTORIQUE ---
    const fetchHistory = async () => {
        if (userId) {
            try {
                const response = await axios.get(`${API_URL}/conversations/${userId}`);
                setConversations(response.data.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)));
            } catch (error) {
                console.error('Erreur historique:', error);
            }
        }
    };

    // --- 2. INITIALISATION DU HOOK D'ENVOI ---
    const { handleSend } = useSendMessages(
        inputValue,
        setInputValue,
        setIsLoading,
        setSentImageUrls,
        setUploadedImageUrls,
        uploadedImageUrls,
        messages,
        setMessages,
        currentConversationId,
        (newId) => {
            // Callback quand une nouvelle conversation est créée
            setCurrentConversationId(newId);
            if (!currentConversationId) {
                // Si on partait de rien, on rafraichit la liste
                fetchHistory();
            }
        },
        fetchHistory, // Pour remonter la conversation en haut de liste
        pdfStats // Pour vérifier le quota avant envoi
    );

    // Scroll automatique vers le bas
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isLoading]);

    // Chargement initial de l'historique
    useEffect(() => {
        fetchHistory();
    }, [userId, historyOpen]);

    // --- 2b. CHARGEMENT DES STATS QUOTA ---
    const fetchPdfStats = async () => {
        if (!userId) return;
        try {
            const response = await axios.get(`${API_URL}/user-pdf-stats/${userId}`);
            if (response.data?.success) {
                setPdfStats(response.data.data);
            }
        } catch (error) {
            console.error('Erreur récupération stats quota:', error);
        }
    };

    useEffect(() => {
        fetchPdfStats();

        // Écouter l'event credits-updated (déclenché après génération PDF)
        const handleCreditsUpdated = () => fetchPdfStats();
        window.addEventListener('credits-updated', handleCreditsUpdated);
        return () => window.removeEventListener('credits-updated', handleCreditsUpdated);
    }, [userId]);


    // --- 3. GESTION DU TITRE (RENOMMAGE) ---
    const startEditing = () => {
        setEditedTitle(currentTitle);
        setIsEditingTitle(true);
    };

    const saveTitle = async () => {
        if (!editedTitle.trim() || !currentConversationId) {
            setIsEditingTitle(false);
            return;
        }

        try {
            // Mise à jour BDD
            await axios.put(`${API_URL}/conversations/${currentConversationId}`, {
                title: editedTitle
            });
            
            // Mise à jour Interface Locale
            setCurrentTitle(editedTitle);
            setConversations(conversations.map(c => 
                c.id === currentConversationId ? { ...c, title: editedTitle } : c
            ));
        } catch (error) {
            console.error("Erreur renommage", error);
            alert("Impossible de renommer la conversation");
        } finally {
            setIsEditingTitle(false);
        }
    };

    // --- 4. GESTION DU CLIC SUR L'HISTORIQUE ---
    const handleHistoryClick = async (conv) => {
        setIsLoading(true);
        try {
            const response = await axios.get(`${API_URL}/conversation/${conv.id}`);
            const fullConversation = response.data;

            if (fullConversation.messages && fullConversation.messages.length > 0) {
                 const formattedMessages = fullConversation.messages.map(m => ({
                    role: (m.role === 'assistant' || m.sender === 'bot' || m.sender === 'assistant') ? 'assistant' : 'user',
                    content: m.content || m.text
                 }));
                 setMessages(formattedMessages);
            } else {
                setMessages([defaultMessage]);
            }

            // Mise à jour des infos courantes
            setCurrentConversationId(conv.id);
            setCurrentTitle(conv.title || "Conversation sans titre");
            setIsConversationCompleted(!!fullConversation.pdfUrl);
            setCurrentPdfUrl(fullConversation.pdfUrl || null);
            setCurrentRating(fullConversation.rating || null);
            setHoverRating(0);

            // Fermeture menu mobile
            if (window.innerWidth < 768) setHistoryOpen(false);

        } catch (error) {
            console.error("Erreur reprise conversation", error);
            // Fallback cache
            if (conv.messages && conv.messages.length > 0) {
                 setMessages(conv.messages.map(m => ({
                    role: (m.role === 'assistant' || m.sender === 'bot' || m.sender === 'assistant') ? 'assistant' : 'user',
                    content: m.content || m.text
                 })));
            }
            setCurrentConversationId(conv.id);
            setCurrentTitle(conv.title || "Conversation sans titre");
        } finally {
            setIsLoading(false);
        }
    };

    // --- 5. SUPPRESSION ---
    const handleDeleteConversation = async (e, conversationId) => {
        e.stopPropagation();
        if(!window.confirm("Voulez-vous vraiment supprimer ce dossier ?")) return;

        try {
            await axios.delete(`${API_URL}/conversations/${conversationId}`);
            setConversations(conversations.filter(c => c.id !== conversationId));
            
            // Si on supprime la conversation active, reset
            if (currentConversationId === conversationId) {
                setMessages([defaultMessage]);
                setCurrentConversationId(null);
                setCurrentTitle("Nouvelle expertise");
                setIsConversationCompleted(false);
                setCurrentPdfUrl(null);
                setCurrentRating(null);
                setHoverRating(0);
            }
        } catch (error) {
            console.error("Erreur suppression", error);
            alert("Impossible de supprimer cette conversation.");
        }
    };

    const rapports = conversations.filter(c => c.pdfUrl);
    const discussions = conversations.filter(c => !c.pdfUrl);

    return (
        <div className="flex h-full w-full bg-[#f4f6f8] relative overflow-hidden">
            
            {/* Overlay Mobile */}
            <div 
                className={`absolute inset-0 bg-black/50 z-20 transition-opacity duration-300 md:hidden ${historyOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                onClick={() => setHistoryOpen(false)}
            ></div>

            {/* ================= SIDEBAR ================= */}
            <div className={`
                z-30 h-full bg-[#1c2143] text-gray-100 shadow-2xl flex flex-col border-r border-gray-700
                transition-all duration-300 ease-in-out
                absolute inset-y-0 left-0 
                ${historyOpen ? 'translate-x-0 w-72' : '-translate-x-full w-72'}
                md:relative md:translate-x-0
                ${historyOpen ? 'md:w-72' : 'md:w-0'}
                overflow-hidden
            `}>
                <div className="w-72 h-full flex flex-col">
                    <div className="p-4 flex items-center justify-between border-b border-gray-700 bg-[#151933]">
                        <h2 className="font-bold text-[#Ceaa5b] text-lg">Mes Dossiers</h2>
                        <button onClick={() => setHistoryOpen(false)} className="md:hidden text-gray-400">
                            <XMarkIcon className="w-6 h-6" />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-6 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-600 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-[#Ceaa5b]">
                        
                        <button 
                            onClick={() => {
                                setMessages([defaultMessage]);
                                setCurrentConversationId(null);
                                setCurrentTitle("Nouvelle expertise");
                                setIsConversationCompleted(false);
                                setCurrentPdfUrl(null);
                                setCurrentRating(null);
                                setHoverRating(0);
                                setIsOver(false);
                                if (window.innerWidth < 768) setHistoryOpen(false);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 bg-[#Ceaa5b] text-[#1c2143] rounded-lg font-bold hover:bg-white transition-all shadow-md"
                        >
                            <PlusIcon className="w-5 h-5" />
                            Nouvelle expertise
                        </button>

                        {/* LISTE DISCUSSIONS */}
                        <div className="space-y-2">
                            <p className="text-xs text-gray-400 uppercase tracking-wider font-bold flex items-center gap-2">
                                <ChatBubbleLeftEllipsisIcon className="w-4 h-4" />
                                Discussions en cours
                            </p>
                            
                            {discussions.length === 0 ? (
                                <p className="text-sm text-gray-600 italic pl-2">Aucune discussion.</p>
                            ) : (
                                discussions.map((conv) => (
                                    <div 
                                        key={conv.id} 
                                        onClick={() => handleHistoryClick(conv)}
                                        className={`
                                            group w-full flex items-center justify-between gap-2 px-3 py-2.5 text-sm rounded-lg transition-colors cursor-pointer border border-transparent
                                            ${currentConversationId === conv.id ? 'bg-white/10 text-white border-gray-600' : 'text-gray-300 hover:bg-white/5 hover:border-gray-700'}
                                        `}
                                    >
                                        <div className="flex items-center gap-3 overflow-hidden">
                                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${currentConversationId === conv.id ? 'bg-[#Ceaa5b]' : 'bg-gray-600'}`}></div>
                                            <div className="overflow-hidden">
                                                {/* ✅ ICI : TRUNCATE pour couper le texte dans la sidebar */}
                                                <span className="block truncate font-medium">
                                                    {conv.title || "Nouvelle discussion"}
                                                </span>
                                                <span className="block text-[10px] text-gray-500">
                                                    {new Date(conv.updatedAt).toLocaleDateString()}
                                                </span>
                                            </div>
                                        </div>
                                        <button 
                                            onClick={(e) => handleDeleteConversation(e, conv.id)}
                                            className="text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity p-1"
                                        >
                                            <TrashIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* LISTE RAPPORTS */}
                        <div className="space-y-2 pt-2 border-t border-gray-700/50">
                            <p className="text-xs text-[#Ceaa5b] uppercase tracking-wider font-bold flex items-center gap-2">
                                <DocumentCheckIcon className="w-4 h-4" />
                                Pré-rapports disponibles
                            </p>
                            {rapports.length === 0 ? (
                                <p className="text-sm text-gray-600 italic pl-2">Aucun rapport.</p>
                            ) : (
                                rapports.map((conv) => (
                                    <div 
                                        key={conv.id} 
                                        onClick={() => handleHistoryClick(conv)}
                                        className="group w-full flex items-center justify-between gap-2 px-3 py-2.5 text-sm text-gray-300 rounded-lg hover:bg-white/5 transition-colors cursor-pointer border border-transparent hover:border-[#Ceaa5b]/30"
                                    >
                                        <div className="flex items-center gap-3 overflow-hidden">
                                            <DocumentCheckIcon className="w-5 h-5 text-[#Ceaa5b] flex-shrink-0" />
                                            <div className="overflow-hidden">
                                                {/* ✅ ICI : TRUNCATE pour couper le texte */}
                                                <span className="block truncate font-medium text-white">
                                                    {conv.title || "Rapport généré"}
                                                </span>
                                                <span className="block text-[10px] text-gray-500">
                                                    {new Date(conv.updatedAt).toLocaleDateString()}
                                                </span>
                                            </div>
                                        </div>
                                        <button 
                                            onClick={(e) => handleDeleteConversation(e, conv.id)}
                                            className="text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity p-1"
                                        >
                                            <TrashIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                    <div className="p-4 border-t border-gray-700 text-xs text-center text-gray-500 bg-[#151933]">
                        AIExpertys &copy; 2024
                    </div>
                </div>
            </div>

            {/* ================= MAIN CHAT AREA ================= */}
            <div className="flex-1 flex flex-col h-full relative transition-all duration-300">
                
                {/* ✅ HEADER DU CHAT AVEC TITRE COMPLET */}
                <div className="flex-none bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between shadow-sm z-10 min-h-[60px]">
                    <div className="flex items-center gap-3 w-full max-w-3xl mx-auto">
                        {isEditingTitle ? (
                            // MODE ÉDITION
                            <div className="flex items-center gap-2 w-full animate-fade-in">
                                <input 
                                    type="text" 
                                    value={editedTitle}
                                    onChange={(e) => setEditedTitle(e.target.value)}
                                    className="flex-1 border-b-2 border-[#Ceaa5b] focus:outline-none text-lg font-semibold text-[#1c2143] px-1 py-0.5 bg-transparent"
                                    autoFocus
                                    onKeyDown={(e) => { if(e.key === 'Enter') saveTitle(); }}
                                />
                                <button onClick={saveTitle} className="text-green-600 hover:text-green-800 p-1">
                                    <CheckIcon className="w-6 h-6" />
                                </button>
                                <button onClick={() => setIsEditingTitle(false)} className="text-gray-400 hover:text-red-500 p-1">
                                    <XMarkIcon className="w-6 h-6" />
                                </button>
                            </div>
                        ) : (
                            // MODE LECTURE (TITRE COMPLET)
                            <div className="flex items-center gap-2 group w-full">
                                {/* ✅ BREAK-WORDS + LEADING-TIGHT : Affiche le titre en entier sur plusieurs lignes si besoin */}
                                <h2 className="text-lg font-bold text-[#1c2143] break-words leading-tight">
                                    {currentTitle}
                                </h2>
                                
                                {currentConversationId && (
                                    <button 
                                        onClick={startEditing} 
                                        className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-[#Ceaa5b] p-1 flex-shrink-0"
                                        title="Renommer la conversation"
                                    >
                                        <PencilSquareIcon className="w-5 h-5" />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* ZONE DE MESSAGES SCROLLABLE */}
                <div className="flex-1 overflow-y-auto p-4 md:p-6 pt-4 scroll-smooth"> 
                    <div className="flex flex-col space-y-6 w-full max-w-3xl mx-auto px-2 md:px-0 pb-4"> 
                        {messages.map((msg, index) => {
                            let textToDisplay = "";
                            if (typeof msg.content === 'string') textToDisplay = msg.content;
                            else if (Array.isArray(msg.content)) {
                                const textPart = msg.content.find(part => part.type === 'text');
                                textToDisplay = textPart ? textPart.text : "";
                            } 
                            else if (typeof msg.content === 'object' && msg.content !== null) {
                                textToDisplay = msg.content.text || JSON.stringify(msg.content);
                            }

                            return (
                                <ChatMessage 
                                    key={index} 
                                    message={{
                                        text: textToDisplay, 
                                        sender: msg.role === 'assistant' ? 'bot' : 'user'
                                    }} 
                                />
                            );
                        })}

                        {/* Animation de chargement (Messenger Style) */}
                        {isLoading && (
                            <div className="flex justify-start mb-4 w-full animate-fade-in pl-1">
                                <div className="flex items-center space-x-1.5 h-8">
                                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                </div>

                {/* ZONE DE SAISIE */}
                <div className="flex-none bg-[#f4f6f8] pb-6 pt-2 z-10">
                    <div className="w-full max-w-4xl mx-auto px-4">
                        {isConversationCompleted && currentPdfUrl ? (
                            /* BANDEAU RAPPORT TERMINÉ */
                            <div className="flex flex-col items-center gap-3 py-4 px-6 bg-white border border-[#Ceaa5b]/30 rounded-xl shadow-sm">
                                <div className="flex items-center gap-4">
                                    <DocumentCheckIcon className="w-6 h-6 text-[#Ceaa5b] flex-shrink-0" />
                                    <span className="text-[#1c2143] font-medium">Ce pré-rapport a été finalisé.</span>
                                    <a
                                        href={currentPdfUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 px-4 py-2 bg-[#1c2143] text-white rounded-lg hover:bg-[#2a3055] transition-all shadow-md text-sm font-medium"
                                    >
                                        <ArrowDownTrayIcon className="w-4 h-4" />
                                        Télécharger le pré-rapport
                                    </a>
                                </div>
                                {/* Notation par étoiles (optionnelle) */}
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-400">
                                        {currentRating ? 'Votre note :' : 'Notez ce pré-rapport :'}
                                    </span>
                                    <div className="flex gap-0.5">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <button
                                                key={star}
                                                onClick={async () => {
                                                    try {
                                                        await axios.put(`${API_URL}/conversations/${currentConversationId}/rate`, {
                                                            rating: star, ratedBy: userId
                                                        }, { headers: { 'auth-token': localStorage.getItem('auth-token') } });
                                                        setCurrentRating(star);
                                                    } catch (e) { console.error('Erreur notation:', e); }
                                                }}
                                                onMouseEnter={() => setHoverRating(star)}
                                                onMouseLeave={() => setHoverRating(0)}
                                                className="focus:outline-none transition-transform hover:scale-110"
                                            >
                                                <svg className={`w-5 h-5 ${
                                                    (hoverRating || currentRating || 0) >= star
                                                        ? 'text-[#Ceaa5b] fill-[#Ceaa5b]'
                                                        : 'text-gray-300 fill-gray-300'
                                                }`} viewBox="0 0 20 20">
                                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                                                </svg>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* ZONE DE SAISIE NORMALE */
                            <>
                                <UploadedImages
                                    uploadedImageUrls={uploadedImageUrls}
                                    largeImageUrls={largeImageUrls}
                                    removeFile={removeFile}
                                />
                                <ChatInput
                                    inputValue={inputValue}
                                    setInputValue={setInputValue}
                                    handleSend={handleSend}
                                    handleImageChange={handleImageChange}
                                    pdfStats={pdfStats}
                                    messages={messages}
                                    sentImageUrls={sentImageUrls}
                                    setShowForm={setShowForm}
                                    setShowModal={setShowModal}
                                    setIsOver={setIsOver}
                                    disabled={isLoading}
                                    conversationId={currentConversationId}
                                    refreshHistory={fetchHistory}
                                    isConversationCompleted={isConversationCompleted}
                                />
                            </>
                        )}
                        <div className="text-center mt-2 text-xs text-gray-400">
                            SophIA peut commettre des erreurs. Envisagez de vérifier les informations importantes.
                        </div>
                    </div>
                </div>
            </div>

            {showModal && <Modal onClose={() => setShowModal(false)} />}
        </div>
    );
};

export default ChatContainer;